import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Polygon, Popup, useMap } from 'react-leaflet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Lock, Unlock, CheckCircle, AlertCircle, Crown,
  DollarSign, Users, TrendingUp, Shield, Zap, RefreshCw,
  ChevronRight, Info, Star, CreditCard, Calendar, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Territory Pricing
const TERRITORY_PRICING = {
  monthly: { price: 500, label: '$500/month', description: 'Flexible monthly billing' },
  annual: { price: 5000, label: '$5,000/year', description: 'Save $1,000 annually', savings: 1000 }
};

// Predefined territories/zones for major Canadian cities
const TERRITORIES = {
  toronto: {
    name: 'Toronto',
    center: [43.6532, -79.3832],
    zoom: 11,
    zones: [
      { id: 'tor-downtown', name: 'Downtown Core', coords: [[43.6700, -79.4100], [43.6700, -79.3600], [43.6400, -79.3600], [43.6400, -79.4100]], leads: '40-60/mo', premium: true },
      { id: 'tor-north-york', name: 'North York', coords: [[43.7800, -79.4500], [43.7800, -79.3500], [43.7200, -79.3500], [43.7200, -79.4500]], leads: '30-45/mo', premium: false },
      { id: 'tor-scarborough', name: 'Scarborough', coords: [[43.7800, -79.2800], [43.7800, -79.1800], [43.7200, -79.1800], [43.7200, -79.2800]], leads: '25-40/mo', premium: false },
      { id: 'tor-etobicoke', name: 'Etobicoke', coords: [[43.6800, -79.5800], [43.6800, -79.4800], [43.6200, -79.4800], [43.6200, -79.5800]], leads: '25-35/mo', premium: false },
      { id: 'tor-midtown', name: 'Midtown', coords: [[43.7100, -79.4100], [43.7100, -79.3700], [43.6750, -79.3700], [43.6750, -79.4100]], leads: '35-50/mo', premium: true },
      { id: 'tor-beaches', name: 'The Beaches', coords: [[43.6800, -79.3000], [43.6800, -79.2600], [43.6500, -79.2600], [43.6500, -79.3000]], leads: '20-30/mo', premium: false },
    ]
  },
  vancouver: {
    name: 'Vancouver',
    center: [49.2827, -123.1207],
    zoom: 11,
    zones: [
      { id: 'van-downtown', name: 'Downtown Vancouver', coords: [[49.2950, -123.1450], [49.2950, -123.1050], [49.2700, -123.1050], [49.2700, -123.1450]], leads: '45-65/mo', premium: true },
      { id: 'van-west', name: 'West Vancouver', coords: [[49.3500, -123.2500], [49.3500, -123.1500], [49.3100, -123.1500], [49.3100, -123.2500]], leads: '35-50/mo', premium: true },
      { id: 'van-burnaby', name: 'Burnaby', coords: [[49.2700, -123.0200], [49.2700, -122.9200], [49.2200, -122.9200], [49.2200, -123.0200]], leads: '30-45/mo', premium: false },
      { id: 'van-richmond', name: 'Richmond', coords: [[49.1900, -123.1800], [49.1900, -123.0800], [49.1400, -123.0800], [49.1400, -123.1800]], leads: '25-40/mo', premium: false },
    ]
  },
  calgary: {
    name: 'Calgary',
    center: [51.0447, -114.0719],
    zoom: 11,
    zones: [
      { id: 'cal-downtown', name: 'Downtown Calgary', coords: [[51.0600, -114.0900], [51.0600, -114.0500], [51.0350, -114.0500], [51.0350, -114.0900]], leads: '35-50/mo', premium: true },
      { id: 'cal-nw', name: 'Northwest Calgary', coords: [[51.1200, -114.1800], [51.1200, -114.0800], [51.0700, -114.0800], [51.0700, -114.1800]], leads: '25-40/mo', premium: false },
      { id: 'cal-sw', name: 'Southwest Calgary', coords: [[51.0200, -114.1500], [51.0200, -114.0500], [50.9700, -114.0500], [50.9700, -114.1500]], leads: '30-45/mo', premium: false },
      { id: 'cal-se', name: 'Southeast Calgary', coords: [[51.0200, -114.0300], [51.0200, -113.9300], [50.9700, -113.9300], [50.9700, -114.0300]], leads: '20-35/mo', premium: false },
    ]
  }
};

// Map center controller component
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

const SponsorTerritory = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedCity, setSelectedCity] = useState('toronto');
  const [claimedZones, setClaimedZones] = useState({});
  const [myTerritories, setMyTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [billingType, setBillingType] = useState('annual');
  const [processingPayment, setProcessingPayment] = useState(false);

  const cityData = TERRITORIES[selectedCity];
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSponsor = user?.role === 'sponsor';

  const fetchTerritoryData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch claimed territories
      const response = await fetch(`${API}/api/territories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const claimed = {};
        data.territories?.forEach(t => {
          if (t.status === 'active') {
            claimed[t.zone_id] = t;
          }
        });
        setClaimedZones(claimed);
      }
      
      // Fetch my territories if sponsor
      if (isSponsor || isAdmin) {
        const myResponse = await fetch(`${API}/api/sponsor/my-territories`, {
          credentials: 'include'
        });
        if (myResponse.ok) {
          const myData = await myResponse.json();
          setMyTerritories(myData.territories || []);
        }
      }
    } catch (error) {
      console.error('Error fetching territories:', error);
    } finally {
      setLoading(false);
    }
  }, [isSponsor, isAdmin]);

  useEffect(() => {
    fetchTerritoryData();
  }, [fetchTerritoryData]);

  // Handle payment success redirect
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      activateTerritory(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const activateTerritory = async (sessionId) => {
    try {
      const response = await fetch(`${API}/api/payments/territory/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Territory "${data.territory.zone_name}" activated!`);
        fetchTerritoryData();
        // Clear URL params
        window.history.replaceState({}, '', '/dashboard/territory');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to activate territory');
      }
    } catch (error) {
      toast.error('Error activating territory');
    }
  };

  const handleClaimZone = (zone) => {
    if (!isSponsor && !isAdmin) {
      toast.error('Only sponsors can claim territories');
      return;
    }
    setSelectedZone(zone);
    setShowPaymentModal(true);
  };

  const initiatePayment = async () => {
    if (!selectedZone) return;
    
    setProcessingPayment(true);
    try {
      const response = await fetch(`${API}/api/payments/territory/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          zone_id: selectedZone.id,
          zone_name: selectedZone.name,
          city: cityData.name,
          billing_type: billingType,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Payment initialization failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getZoneStatus = (zone) => {
    const claimed = claimedZones[zone.id];
    if (!claimed) return 'available';
    if (claimed.user_id === user?.user_id) return 'owned';
    return 'claimed';
  };

  const getZoneColor = (zone) => {
    const status = getZoneStatus(zone);
    if (status === 'owned') return '#10B981'; // Green - you own it
    if (status === 'claimed') return '#EF4444'; // Red - taken
    if (zone.premium) return '#F59E0B'; // Amber - premium available
    return '#6366F1'; // Indigo - standard available
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
            Territory Selection
          </h1>
          <p className="text-neutral-500 mt-1">
            Claim exclusive territories to receive high-intent homeowner leads
          </p>
        </div>
        
        {/* Pricing Badge */}
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-r from-[#FF3B30]/10 to-orange-100 rounded-xl px-4 py-2">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#FF3B30]" />
              <span className="font-semibold text-neutral-900">
                $500/mo or $5,000/yr
              </span>
              <Badge className="bg-green-100 text-green-700">Save $1K</Badge>
            </div>
          </div>
          <Button
            onClick={fetchTerritoryData}
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="refresh-territories-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* My Territories Summary */}
      {myTerritories.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="font-semibold text-green-800 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Your Active Territories ({myTerritories.length})
          </h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {myTerritories.map(t => (
              <Badge key={t.territory_id} className="bg-green-100 text-green-700">
                {t.zone_name} - {t.city}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* City Selector */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TERRITORIES).map(([key, city]) => (
          <Button
            key={key}
            variant={selectedCity === key ? 'default' : 'outline'}
            onClick={() => setSelectedCity(key)}
            className={selectedCity === key ? 'bg-[#FF3B30] hover:bg-[#DC2626]' : ''}
            data-testid={`city-${key}`}
          >
            <MapPin className="w-4 h-4 mr-2" />
            {city.name}
          </Button>
        ))}
      </div>

      {/* Map and Legend */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
          <div className="h-[500px]">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
              </div>
            ) : (
              <MapContainer
                center={cityData.center}
                zoom={cityData.zoom}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <MapController center={cityData.center} zoom={cityData.zoom} />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
                {cityData.zones.map((zone) => {
                  const status = getZoneStatus(zone);
                  return (
                    <Polygon
                      key={zone.id}
                      positions={zone.coords}
                      pathOptions={{
                        color: getZoneColor(zone),
                        fillColor: getZoneColor(zone),
                        fillOpacity: status === 'owned' ? 0.5 : 0.3,
                        weight: status === 'owned' ? 3 : 2
                      }}
                      eventHandlers={{
                        click: () => status === 'available' && handleClaimZone(zone)
                      }}
                    >
                      <Popup>
                        <div className="p-2 min-w-[200px]">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-lg">{zone.name}</h4>
                            {zone.premium && <Crown className="w-5 h-5 text-amber-500" />}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-neutral-400" />
                              <span>Est. {zone.leads} leads</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-neutral-400" />
                              <span>$500/mo or $5,000/yr</span>
                            </div>
                          </div>
                          
                          {status === 'available' && (isSponsor || isAdmin) && (
                            <Button
                              onClick={() => handleClaimZone(zone)}
                              className="w-full mt-3 bg-[#FF3B30] hover:bg-[#DC2626]"
                              size="sm"
                              data-testid={`claim-${zone.id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              Claim Territory
                            </Button>
                          )}
                          
                          {status === 'owned' && (
                            <div className="mt-3 p-2 bg-green-50 rounded-lg text-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                              <span className="text-green-700 text-sm font-medium">You own this territory</span>
                            </div>
                          )}
                          
                          {status === 'claimed' && (
                            <div className="mt-3 p-2 bg-red-50 rounded-lg text-center">
                              <Lock className="w-5 h-5 text-red-500 mx-auto mb-1" />
                              <span className="text-red-600 text-sm">Already claimed</span>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Polygon>
                  );
                })}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Legend & Stats */}
        <div className="space-y-4">
          {/* Legend */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4">
            <h3 className="font-semibold text-neutral-900 mb-3">Legend</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-indigo-500"></div>
                <span className="text-sm text-neutral-600">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500"></div>
                <span className="text-sm text-neutral-600">Premium Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="text-sm text-neutral-600">Your Territory</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-sm text-neutral-600">Claimed by Others</span>
              </div>
            </div>
          </div>

          {/* Pricing Info */}
          <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-xl p-4 text-white">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Territory Pricing
            </h3>
            <div className="space-y-3">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Monthly</span>
                  <span className="font-bold text-lg">$500</span>
                </div>
                <p className="text-xs text-white/60 mt-1">Flexible, cancel anytime</p>
              </div>
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-3 border border-green-400/30">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Annual</span>
                  <div className="text-right">
                    <span className="font-bold text-lg">$5,000</span>
                    <Badge className="ml-2 bg-green-500 text-white text-[10px]">SAVE $1K</Badge>
                  </div>
                </div>
                <p className="text-xs text-white/60 mt-1">Best value - 2 months free!</p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4">
            <h3 className="font-semibold text-neutral-900 mb-3">What You Get</h3>
            <div className="space-y-2">
              {[
                { icon: Users, text: 'Exclusive leads in your zone' },
                { icon: Shield, text: 'No competing sponsors' },
                { icon: TrendingUp, text: 'AI-scored lead quality' },
                { icon: Zap, text: 'Instant lead notifications' }
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-sm text-neutral-600">
                  <item.icon className="w-4 h-4 text-[#FF3B30]" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedZone && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-neutral-900">Claim Territory</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-neutral-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-900">{selectedZone.name}</h4>
                    <p className="text-sm text-neutral-500">{cityData.name} • Est. {selectedZone.leads} leads</p>
                  </div>
                  {selectedZone.premium && <Crown className="w-5 h-5 text-amber-500" />}
                </div>
              </div>

              {/* Billing Type Selection */}
              <div className="space-y-3 mb-6">
                <label className="text-sm font-medium text-neutral-700">Select Billing Plan</label>
                
                <div
                  onClick={() => setBillingType('annual')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    billingType === 'annual' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="font-semibold text-neutral-900">Annual</span>
                        <p className="text-xs text-neutral-500">Pay yearly, save more</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-lg text-neutral-900">$5,000</span>
                      <Badge className="ml-2 bg-green-100 text-green-700 text-[10px]">BEST VALUE</Badge>
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => setBillingType('monthly')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    billingType === 'monthly' 
                      ? 'border-[#FF3B30] bg-red-50' 
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-[#FF3B30]" />
                      <div>
                        <span className="font-semibold text-neutral-900">Monthly</span>
                        <p className="text-xs text-neutral-500">Flexible, cancel anytime</p>
                      </div>
                    </div>
                    <span className="font-bold text-lg text-neutral-900">$500/mo</span>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-neutral-100 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600">Total</span>
                  <span className="text-2xl font-bold text-neutral-900">
                    ${billingType === 'annual' ? '5,000' : '500'}
                    {billingType === 'monthly' && <span className="text-sm font-normal text-neutral-500">/mo</span>}
                  </span>
                </div>
                {billingType === 'annual' && (
                  <p className="text-sm text-green-600 mt-1">You save $1,000 compared to monthly!</p>
                )}
              </div>

              <Button
                onClick={initiatePayment}
                disabled={processingPayment}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white font-semibold"
                data-testid="proceed-payment-btn"
              >
                {processingPayment ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Proceed to Payment
                  </span>
                )}
              </Button>

              <p className="text-xs text-neutral-500 text-center mt-3">
                Secure payment powered by Stripe
              </p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SponsorTerritory;
